import jwt from 'jsonwebtoken';
import {
  ZOOCASA_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  getTenantById,
  TenantId,
  EXP_COMMERCIAL_CA_TENANT_ID,
  AGNT_INC_TENANT_ID,
} from 'tenants';

type CacheEntry = { token: string; exp: number };
const cache: Record<TenantId, CacheEntry | undefined> = Object.create(null);

const SIX_DAYS = 60 * 60 * 24 * 6;

const origins: Record<TenantId, readonly string[]> = {
  [ZOOCASA_TENANT_ID]: getTenantById(ZOOCASA_TENANT_ID).hosts,
  [EXP_REALTY_CA_TENANT_ID]: getTenantById(EXP_REALTY_CA_TENANT_ID).hosts,
  [EXP_REALTY_US_TENANT_ID]: getTenantById(EXP_REALTY_US_TENANT_ID).hosts,
  [EXP_IN_SERVICE_TENANT_ID]: getTenantById(EXP_IN_SERVICE_TENANT_ID).hosts,
  [EXP_COMMERCIAL_TENANT_ID]: getTenantById(EXP_COMMERCIAL_TENANT_ID).hosts,
  [EXP_COMMERCIAL_CA_TENANT_ID]: getTenantById(EXP_COMMERCIAL_CA_TENANT_ID).hosts,
  [AGNT_INC_TENANT_ID]: getTenantById(AGNT_INC_TENANT_ID).hosts,
};

// Local dev URLs are accepted by MapKit JS as long as they are
// included verbatim in the `origin` claim.
const LOCAL_ORIGINS = [
  'http://localhost:4200',
  'http://127.0.0.1:4200',
];

/**
 * Get the MapKit token for the given tenant. The token is cached for 6 days.
 *
 * @param tenant The {@link TenantId tenantId} to get the MapKit token for.
 * @returns The MapKit token for the given tenant.
 */
export function getMapKitToken(tenant: TenantId): string {
  const now = Math.floor(Date.now() / 1000);
  const cached = cache[tenant];
  if (cached && cached.exp - 60 > now) return cached.token; // still valid

  const teamId = process.env.APPLE_MAPKIT_TEAM_ID; // Apple Developer 10-char team ID
  const keyId = process.env.APPLE_MAPKIT_KEY_ID; // Key ID of your .p8
  const key = Buffer
    .from(process.env.APPLE_MAPKIT_PRIVATE_KEY_BASE64, 'base64')
    .toString('utf8'); // raw .p8 PEM

  const iat = now;
  const exp = now + SIX_DAYS;
  const payload = {
    iss: teamId,
    iat,
    exp,
    origin:
      process.env.NODE_ENV === 'production'
        ? origins[tenant]
        : [...origins[tenant], ...LOCAL_ORIGINS],
  };
  const header = { alg: 'ES256' as const, kid: keyId };

  const token = jwt.sign(payload, key, { algorithm: 'ES256', header });
  cache[tenant] = { token, exp };
  return token;
}